import { getSupportArticleUrl } from '@frameio/utils';
import type {
  IconToken,
  TooltipAction,
  TooltipActionAlignment,
  TooltipAudience,
  TooltipLinkContent,
  TooltipMedia,
  TooltipPresentation,
  TooltipVariant,
} from '../stores/tooltipStore.types';
import TEXT from './generated/TEXT/Tooltips';
import {
  getIntegrationExportCtaTarget,
  INTEGRATION_EXPORT_ONBOARDING_BASE,
} from './integrations/export/export';
import {
  getIntegrationShareCtaTarget,
  INTEGRATION_SHARE_ONBOARDING_BASE,
} from './integrations/share/share';

const SHARES_SUPPORT_ARTICLE = '9105232-shares-in-frame-io';
const DRIVE_SUPPORT_ARTICLE = '14501614-frame-io-drive-overview';
const LABS_SUPPORT_ARTICLE = '15305881-frame-io-labs';

TEXT.DEFINE({
  /** AI Assistant discovery tooltip */
  AI_ASSISTANT: {
    /** Title for the AI Assistant tooltip */
    title: 'Open AI Assistant',
  },
  /** Share button tooltip */
  SHARE_BUTTON: {
    /** Title for share button tooltip */
    title: 'Create a Share Link',
    /** Description for share button tooltip - part 1 */
    descriptionPart1: 'Present your work in a customizable',
    /** Description for share button tooltip - part 2 */
    descriptionPart2: 'layout. Send it to get Feedback.',
  },
  /** Upload button tooltip */
  UPLOAD_BUTTON: {
    /** Title for upload button tooltip */
    title: 'Add Files to Collaborate',
    /** Description for upload button tooltip */
    description: 'Upload files and folders you want to collaborate on.',
  },
  /** Comment composer tooltip */
  COMMENT_COMPOSER: {
    /** Title for comment composer tooltip */
    title: 'Start Typing to Leave Feedback',
    /** Description for comment composer tooltip */
    description: 'Add your frame-accurate comments here.',
  },
  /** Annotation button tooltip */
  ANNOTATION_BUTTON: {
    /** Title for annotation button tooltip */
    title: 'Mark up a Frame',
    /** Description for annotation button tooltip */
    description:
      'Use the annotation tool to highlight important areas on a frame.',
  },
  /** Fields tab tooltip */
  FIELDS_TAB: {
    /** Title for fields tab tooltip */
    title: 'Metadata and Properties',
    /** Description for fields tab tooltip */
    description:
      'View details such as bit rate, status, and viewer history for this asset.',
  },
  /** Text annotations tooltip */
  TEXT_ANNOTATIONS: {
    /** Title for text annotations tooltip */
    title: 'Suggest an Edit',
    /** Description for text annotations tooltip */
    description: 'Turn on text suggestions and begin by highlighting text.',
  },
  /** Share comment composer tooltip for video frames */
  SHARE_COMMENT_COMPOSER: {
    /** Title for share comment composer tooltip */
    title: 'Have thoughts on this frame?',
    /** Description for share comment composer tooltip - part 1 */
    descriptionPart1:
      'Your comments stay linked to the right moment, so the team knows exactly what you mean.',
    /** Description for share comment composer tooltip - part 2 */
    descriptionPart2: 'Use C to comment at the current time.',
  },
  /** Share comment composer tooltip for images */
  SHARE_COMMENT_COMPOSER_IMAGE: {
    /** Title for share comment composer image tooltip */
    title: 'Have thoughts on this asset?',
    /** Description for share comment composer image tooltip */
    description:
      'Comment on the entire image or anchor them to a specific spot.',
  },
  /** Share comment composer tooltip for PDFs */
  SHARE_COMMENT_COMPOSER_PDF: {
    /** Title for share comment composer PDF tooltip */
    title: 'Have thoughts on this page?',
    /** Description for share comment composer PDF tooltip - part 1 */
    descriptionPart1:
      'Your comments stay linked to the right page so the team knows exactly what you mean.',
    /** Description for share comment composer PDF tooltip - part 2 */
    descriptionPart2: 'Use C to comment on the current page.',
  },
  /** Share links list tooltip in share builder */
  SHARE_LINKS: {
    /** Title for share links list tooltip */
    title: 'Share Links',
    /** Description for share links list tooltip */
    description:
      'All the Shares you create live here. Add assets directly by dragging and dropping.',
    /** Label for watch video link */
    linkLabel: 'Watch the Video',
  },
  /** Open share link tooltip in share builder */
  OPEN_SHARE_LINK: {
    /** Title for open share link tooltip */
    title: 'View Your Share Link',
    /** Description for open share link tooltip */
    description: 'Open your Share or copy the link to send for review.',
  },
  /** Account avatar tooltip (Japanese audience) */
  ACCOUNT_MULTI_LANGUAGE: {
    /** Title for account avatar tooltip */
    title: 'Frame.io is now available in more languages.',
    /** Description for account avatar tooltip */
    description:
      'Switch anytime using the language picker in account settings.',
  },
  /** Mount project button tooltip */
  MOUNT_PROJECT_BUTTON: {
    /** Title for mount project button tooltip */
    title: 'Work locally with Frame.io Drive',
    /** Description for mount project button tooltip */
    description: 'Open projects directly on your desktop.',
    /** Link label for mount project button tooltip */
    linkLabel: 'Learn more',
  },
  /** Enable Mounted Storage button tooltip */
  ENABLE_MOUNTED_STORAGE_BUTTON: {
    /** Title for enable mounted storage button tooltip */
    title: 'Let your team work locally with Frame.io Drive',
    /** Description for enable mounted storage button tooltip */
    description:
      'Enable Mounted Storage to let members with edit access or higher open projects directly on their desktops with Frame.io Drive.',
  },
  /** Labs nav item tooltip */
  LABS_NAV: {
    /** Title for labs nav tooltip */
    title: "Shape what's next with Frame.io Labs",
    /** Description for labs nav tooltip */
    description:
      "Get early access to experimental features and share feedback to help improve what we're building.",
    /** Link label for labs nav tooltip */
    linkLabel: 'Learn more',
  },
  /** Export sequence button tooltip in Premiere Pro sequences tab */
  EXPORT_SEQUENCE_BUTTON: {
    /** Title for export sequence button tooltip */
    title: 'Export your sequence to Frame.io.',
    /** Description for export sequence button tooltip */
    description:
      'Upload sequences so you can share them for review without leaving Premiere.',
  },
  /** Export composition button tooltip in After Effects compositions tab */
  EXPORT_COMPOSITION_BUTTON: {
    /** Title for export composition button tooltip */
    title: 'Export your render queue to Frame.io.',
    /** Description for export composition button tooltip */
    description:
      'Upload compositions so you can share them for review without leaving After Effects.',
  },
  /** Share sequence button tooltip in Premiere Pro sequences tab */
  SHARE_SEQUENCE_BUTTON: {
    /** Title for share sequence button tooltip */
    title: 'Share your sequence for review',
    /** Description for share sequence button tooltip */
    description: 'Anyone with the link can watch and comment.',
  },
  /** Share composition button tooltip in After Effects compositions tab */
  SHARE_COMPOSITION_BUTTON: {
    /** Title for share composition button tooltip */
    title: 'Share your composition for review',
    /** Description for share composition button tooltip */
    description: 'Anyone with the link can watch and comment.',
  },
  /** Save as list button tooltip in the quickshare modal */
  SHARE_LIST: {
    /** Tooltip heading for the Save as list button in the share modal */
    title: 'Speed Up Sharing',
    /** Tooltip body explaining that share lists let you invite multiple collaborators at once */
    description:
      'Use lists to invite collaborators to a Share Link at one time.',
    /** Button label to dismiss the Save as list tooltip */
    actionLabel: 'Got it',
  },
  /** Create upload link button tooltip in the finder bottom bar */
  CREATE_UPLOAD_LINK: {
    /** Title for the create upload link tooltip */
    title: 'Let anyone upload directly to a folder',
    /** Description explaining what upload links do */
    description:
      'Share an Upload Link and let freelancers, vendors, or clients drop files straight into the right folder. Secure and ready to go — no account needed.',
    /** Button label to dismiss the create upload link tooltip */
    actionLabel: 'OK',
  },
  /** Discovery tooltip pointing at the first editable shortcut in the help dialog */
  CUSTOM_SHORTCUTS_CAPTURE: {
    /** Title for the custom-shortcuts discovery tooltip */
    title: 'Customize your shortcuts',
    /** Description explaining how to edit a shortcut inline */
    description:
      'Hover over any row to edit or clear a shortcut. Reset to defaults any time.',
  },
  /** Version history discovery tooltip anchored to the version pill in the viewer */
  VERSION_HISTORY: {
    /** Title for the version history tooltip */
    title: 'See All Saves',
    /** Description explaining that Frame.io Drive saves appear in version history */
    description: 'Every save from Frame.io Drive appears in Version History.',
    /** Button label that opens the version history modal */
    viewHistoryLabel: 'View Version History',
  },
});

export const TOOLTIP_IDS = {
  ACCOUNT_MULTI_LANGUAGE: 'account-avatar',
  AI_ASSISTANT: 'ai-assistant',
  ANNOTATION_BUTTON: 'annotation-button',
  BACK_BUTTON: 'back-button',
  COMMENT_COMPOSER: 'comment-composer',
  CREATE_UPLOAD_LINK: 'create-upload-link',
  CUSTOM_SHORTCUTS_CAPTURE: 'custom-shortcuts-capture',
  ENABLE_MOUNTED_STORAGE_BUTTON: 'enable-mounted-storage-button',
  EXPORT_COMPOSITION_BUTTON: 'export-composition-button',
  EXPORT_SEQUENCE_BUTTON: 'export-sequence-button',
  FIELDS_TAB: 'fields-tab',
  LABS_NAV: 'labs-nav',
  MOUNT_PROJECT_BUTTON: 'mount-project-button',
  OPEN_SHARE_LINK: 'open-share-link',
  SHARE_BUTTON: 'share-button',
  SHARE_COMMENT_COMPOSER: 'share-comment-composer',
  SHARE_COMMENT_COMPOSER_IMAGE: 'share-comment-composer-image',
  SHARE_COMMENT_COMPOSER_PDF: 'share-comment-composer-pdf',
  SHARE_COMPOSITION_BUTTON: 'share-composition-button',
  SHARE_LINKS: 'share-links',
  SHARE_LIST: 'share-list',
  SHARE_SEQUENCE_BUTTON: 'share-sequence-button',
  TEXT_ANNOTATIONS: 'text-annotations',
  TIME_FORMAT: 'time-format',
  UPLOAD_BUTTON: 'upload-button',
  VERSION_HISTORY: 'version-history',
} as const;

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'left-start'
  | 'right'
  | 'right-start';

type PlacementByBreakpoint = {
  mobile?: Placement;
  desktop?: Placement;
};

type BaseTooltipGroupConfig = {
  routes?: string[]; // default: [] (eligible on all routes)
  showAfterVisitNumber?: number; // default: 1
  maxDisplayCount?: number; // default: 2
  delayMs?: number; // default: 0ms
  autoDismissMs?: number;
  precedence?: number; // Lower is higher priority; default is 0
  displayAfterTooltipsIds?: string[];
  variant: TooltipVariant;
  presentation?: TooltipPresentation; // default: card
  disableOnMobile?: boolean;
  // If true, this tooltip is excluded from route-based auto-queueing and
  // should only be shown via an explicit `tooltipStore.openTooltip(...)` call.
  // Use this for targets that are not always in the DOM (menus, popovers, dialogs).
  manualOpenOnly?: boolean;
  /** Eligibility feature: 'onboarding' (first-login) or feature (e.g. 'reframe') gated by feature flag. Defaults to 'onboarding'. */
  feature?: string;
  /** Audience targeting criteria. All criteria must match (AND). Omit or leave empty to show to all users. */
  audience?: TooltipAudience[];
};

type TooltipContent = {
  icon?: IconToken;
  showBetaChip?: boolean;
  media?: TooltipMedia;
  link?: TooltipLinkContent;
  action?: TooltipAction;
  actionAlignment?: TooltipActionAlignment;
  placement?: Placement;
  placementByBreakpoint?: PlacementByBreakpoint;
  maxWidth?: string;
};

export type TooltipDefinition = BaseTooltipGroupConfig &
  TooltipContent & {
    id: string;
    title: string;
    description?: string;
    target?: string;
    showHighlight?: boolean; // default: true
    onDismiss?: () => void;
  };

export type TooltipGroupStep = TooltipContent & {
  id: string;
  title: string;
  description?: string;
  target?: string;
  showHighlight?: boolean; // default: true
};

export type TooltipGroup = BaseTooltipGroupConfig & {
  type: 'group';
  id: string;
  steps: TooltipGroupStep[];
};

export const TOOLTIPS: (TooltipDefinition | TooltipGroup)[] = [
  {
    id: TOOLTIP_IDS.AI_ASSISTANT,
    title: TEXT.AI_ASSISTANT.title,
    target: '[data-onboarding-anchor="ai-assistant"]',
    maxDisplayCount: 1,
    autoDismissMs: 2000,
    manualOpenOnly: true,
    presentation: 'compact',
    placement: 'bottom',
    showHighlight: false,
    feature: 'aiAssistant',
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.SHARE_BUTTON,
    title: TEXT.SHARE_BUTTON.title,
    description: `${TEXT.SHARE_BUTTON.descriptionPart1}\n${TEXT.SHARE_BUTTON.descriptionPart2}`,
    delayMs: 1000,
    showAfterVisitNumber: 2,
    target: '[data-testid="menu-bar-share-button"]',
    routes: ['/project/*'],
    precedence: 2,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.UPLOAD_BUTTON,
    title: TEXT.UPLOAD_BUTTON.title,
    description: TEXT.UPLOAD_BUTTON.description,
    delayMs: 1000,
    showAfterVisitNumber: 2,
    target: '[data-testid="assets-panel-menu-bar--add-button"]',
    routes: ['/project/*'],
    precedence: 1,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.ACCOUNT_MULTI_LANGUAGE,
    title: TEXT.ACCOUNT_MULTI_LANGUAGE.title,
    description: TEXT.ACCOUNT_MULTI_LANGUAGE.description,
    delayMs: 1000,
    disableOnMobile: true,
    showAfterVisitNumber: 3,
    target: '[data-testid="account-avatar"]',
    placement: 'right',
    precedence: 10,
    variant: 'Body',
    media: {
      mediaType: 'video',
      src: 'http://embed.wistia.com/deliveries/52b4a5057d5f377ebb6794491228d62145a43e27.mp4',
      alt: 'Japanese account avatar',
      aspectRatio: 1200 / 704,
    },
  },
  {
    id: TOOLTIP_IDS.COMMENT_COMPOSER,
    title: TEXT.COMMENT_COMPOSER.title,
    description: TEXT.COMMENT_COMPOSER.description,
    delayMs: 1000,
    showAfterVisitNumber: 1000000,
    target: '[data-testid="create-comment-composer-container"]',
    routes: ['/project/*/view/*'],
    precedence: 1,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.ANNOTATION_BUTTON,
    title: TEXT.ANNOTATION_BUTTON.title,
    description: TEXT.ANNOTATION_BUTTON.description,
    delayMs: 1000,
    showAfterVisitNumber: 5,
    target: '[data-testid="icon-button-annotation-asset-details-panel"]',
    routes: ['/project/*/view/*'],
    precedence: 5,
    displayAfterTooltipsIds: [TOOLTIP_IDS.COMMENT_COMPOSER],
    showHighlight: false,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.FIELDS_TAB,
    title: TEXT.FIELDS_TAB.title,
    description: TEXT.FIELDS_TAB.description,
    placement: 'bottom',
    delayMs: 1000,
    showAfterVisitNumber: 10,
    target: '[id="tab-asset-details-panel-1"]',
    routes: ['/project/*/view/*'],
    precedence: 6,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.TEXT_ANNOTATIONS,
    title: TEXT.TEXT_ANNOTATIONS.title,
    description: TEXT.TEXT_ANNOTATIONS.description,
    placement: 'bottom',
    delayMs: 1000,
    target: '[data-testid="text-suggestion-toggle-button"]',
    routes: ['/project/*/view/*'],
    precedence: 7,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.SHARE_COMMENT_COMPOSER,
    title: TEXT.SHARE_COMMENT_COMPOSER.title,
    description: `${TEXT.SHARE_COMMENT_COMPOSER.descriptionPart1}\n\n${TEXT.SHARE_COMMENT_COMPOSER.descriptionPart2}`,
    delayMs: 1000,
    showAfterVisitNumber: 1000000,
    target: '[data-testid="create-comment-composer-container"]',
    routes: ['/share/*/view/*'],
    placementByBreakpoint: {
      mobile: 'top',
      desktop: 'left',
    },
    precedence: 1,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.SHARE_COMMENT_COMPOSER_IMAGE,
    title: TEXT.SHARE_COMMENT_COMPOSER_IMAGE.title,
    description: TEXT.SHARE_COMMENT_COMPOSER_IMAGE.description,
    delayMs: 1000,
    showAfterVisitNumber: 1000000,
    target: '[data-testid="create-comment-composer-container"]',
    routes: ['/share/*/view/*'],
    placementByBreakpoint: {
      mobile: 'top',
      desktop: 'left',
    },
    precedence: 1,
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.SHARE_COMMENT_COMPOSER_PDF,
    title: TEXT.SHARE_COMMENT_COMPOSER_PDF.title,
    description: `${TEXT.SHARE_COMMENT_COMPOSER_PDF.descriptionPart1}\n\n${TEXT.SHARE_COMMENT_COMPOSER_PDF.descriptionPart2}`,
    delayMs: 1000,
    showAfterVisitNumber: 1000000,
    target: '[data-testid="create-comment-composer-container"]',
    routes: ['/share/*/view/*'],
    placementByBreakpoint: {
      mobile: 'top',
      desktop: 'left',
    },
    precedence: 1,
    variant: 'Body',
  },
  {
    type: 'group',
    id: 'share-builder',
    // Excludes share builder viewer routes
    routes: ['^(?!.*/view/)/project/[^/]+/share-builder/[^/]+(/.*)?$'],
    delayMs: 1000,
    showAfterVisitNumber: 1,
    precedence: 1,
    variant: 'Body',
    disableOnMobile: true,
    steps: [
      {
        id: TOOLTIP_IDS.SHARE_LINKS,
        title: TEXT.SHARE_LINKS.title,
        description: TEXT.SHARE_LINKS.description,
        target: '[data-testid="share-links-list"]',
        placement: 'right',
        link: {
          label: TEXT.SHARE_LINKS.linkLabel,
          href: getSupportArticleUrl(SHARES_SUPPORT_ARTICLE),
          openInNewTab: true,
          icon: 'IconPlay',
        },
      },
      {
        id: TOOLTIP_IDS.OPEN_SHARE_LINK,
        title: TEXT.OPEN_SHARE_LINK.title,
        description: TEXT.OPEN_SHARE_LINK.description,
        target: '[data-testid="share-buttons-container"]',
      },
    ],
  },
  {
    id: TOOLTIP_IDS.EXPORT_SEQUENCE_BUTTON,
    title: TEXT.EXPORT_SEQUENCE_BUTTON.title,
    description: TEXT.EXPORT_SEQUENCE_BUTTON.description,
    ...INTEGRATION_EXPORT_ONBOARDING_BASE,
    audience: [{ type: 'client', clients: ['premiere'] }],
    target: getIntegrationExportCtaTarget('premiere'),
  },
  {
    id: TOOLTIP_IDS.EXPORT_COMPOSITION_BUTTON,
    title: TEXT.EXPORT_COMPOSITION_BUTTON.title,
    description: TEXT.EXPORT_COMPOSITION_BUTTON.description,
    ...INTEGRATION_EXPORT_ONBOARDING_BASE,
    audience: [{ type: 'client', clients: ['aftereffects'] }],
    target: getIntegrationExportCtaTarget('aftereffects'),
  },
  {
    id: TOOLTIP_IDS.SHARE_SEQUENCE_BUTTON,
    title: TEXT.SHARE_SEQUENCE_BUTTON.title,
    description: TEXT.SHARE_SEQUENCE_BUTTON.description,
    ...INTEGRATION_SHARE_ONBOARDING_BASE,
    audience: [{ type: 'client', clients: ['premiere'] }],
    target: getIntegrationShareCtaTarget('premiere'),
  },
  {
    id: TOOLTIP_IDS.SHARE_COMPOSITION_BUTTON,
    title: TEXT.SHARE_COMPOSITION_BUTTON.title,
    description: TEXT.SHARE_COMPOSITION_BUTTON.description,
    ...INTEGRATION_SHARE_ONBOARDING_BASE,
    audience: [{ type: 'client', clients: ['aftereffects'] }],
    target: getIntegrationShareCtaTarget('aftereffects'),
  },
  {
    id: TOOLTIP_IDS.MOUNT_PROJECT_BUTTON,
    title: TEXT.MOUNT_PROJECT_BUTTON.title,
    description: TEXT.MOUNT_PROJECT_BUTTON.description,
    disableOnMobile: true,
    feature: 'mountableStorage',
    link: {
      label: TEXT.MOUNT_PROJECT_BUTTON.linkLabel,
      href: getSupportArticleUrl(DRIVE_SUPPORT_ARTICLE),
      openInNewTab: true,
      icon: 'IconCustomFieldLink',
    },
    media: {
      mediaType: 'video',
      src: 'https://embed.wistia.com/deliveries/918a1e7d388c64ad6f7c43f1b54ca272f32ca75c.mp4',
      alt: 'Mounted storage',
      aspectRatio: 16 / 9,
    },
    placement: 'bottom',
    precedence: 5,
    routes: ['/project/*'],
    showAfterVisitNumber: 4,
    target: '[data-testid="open-on-desktop-button"]',
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.ENABLE_MOUNTED_STORAGE_BUTTON,
    title: TEXT.ENABLE_MOUNTED_STORAGE_BUTTON.title,
    description: TEXT.ENABLE_MOUNTED_STORAGE_BUTTON.description,
    disableOnMobile: true,
    feature: 'mountableStorage',
    manualOpenOnly: true,
    media: {
      mediaType: 'video',
      src: 'https://embed.wistia.com/deliveries/918a1e7d388c64ad6f7c43f1b54ca272f32ca75c.mp4',
      alt: 'Mounted storage',
      aspectRatio: 16 / 9,
    },
    placement: 'right-start',
    routes: ['/settings/usage'],
    showAfterVisitNumber: 1,
    target: '[data-testid="onboarding-enable-mounted-storage-button"]',
    variant: 'Body',
  },
  {
    id: TOOLTIP_IDS.LABS_NAV,
    title: TEXT.LABS_NAV.title,
    description: TEXT.LABS_NAV.description,
    target: '[data-testid="settings-nav-labs"]',
    routes: ['/settings/profile'],
    placement: 'right',
    maxWidth: '360px',
    showAfterVisitNumber: 1,
    feature: 'labs',
    variant: 'Body',
    link: {
      label: TEXT.LABS_NAV.linkLabel,
      href: getSupportArticleUrl(LABS_SUPPORT_ARTICLE),
      openInNewTab: true,
      icon: 'IconCustomFieldLink',
    },
    media: {
      mediaType: 'image',
      src: '/images/tooltip-labs.png',
      alt: 'Frame.io Labs',
      aspectRatio: 1928 / 1118,
    },
  },
  {
    id: TOOLTIP_IDS.SHARE_LIST,
    title: TEXT.SHARE_LIST.title,
    description: TEXT.SHARE_LIST.description,
    delayMs: 500,
    showAfterVisitNumber: 1,
    target: '[data-testid="save-as-list-button"]',
    placement: 'left-start',
    precedence: 1,
    manualOpenOnly: true,
    showHighlight: false,
    feature: 'shareLists',
    variant: 'Body',
    maxWidth: '360px',
    media: {
      mediaType: 'image',
      src: '/images/tooltip-share-list.png',
      alt: 'Share list example',
      aspectRatio: 336 / 198,
    },
    action: {
      kind: 'command',
      label: TEXT.SHARE_LIST.actionLabel,
      command: 'dismiss',
    },
  },
  {
    id: TOOLTIP_IDS.CREATE_UPLOAD_LINK,
    title: TEXT.CREATE_UPLOAD_LINK.title,
    description: TEXT.CREATE_UPLOAD_LINK.description,
    delayMs: 500,
    showAfterVisitNumber: 1,
    maxDisplayCount: 2,
    target: '[data-testid="bottom-bar-upload-link"]',
    placement: 'top',
    manualOpenOnly: true,
    feature: 'uploadLinks',
    variant: 'Body',
    maxWidth: '360px',
    showBetaChip: true,
    media: {
      mediaType: 'image',
      src: '/images/tooltip-upload-links.png',
      alt: 'Create an upload link',
      aspectRatio: 720 / 424,
    },
    actionAlignment: 'end',
    action: {
      kind: 'command',
      label: TEXT.CREATE_UPLOAD_LINK.actionLabel,
      command: 'dismiss',
    },
  },
  {
    id: TOOLTIP_IDS.CUSTOM_SHORTCUTS_CAPTURE,
    title: TEXT.CUSTOM_SHORTCUTS_CAPTURE.title,
    description: TEXT.CUSTOM_SHORTCUTS_CAPTURE.description,
    // Anchors to the full row (the first customizable command — typically the
    // second row, since the first isn't customizable) so the highlight overlay
    // covers the entire row rather than just its keycaps.
    target: '[data-onboarding-anchor="custom-shortcuts-capture"]',
    placement: 'bottom',
    delayMs: 200,
    showAfterVisitNumber: 1,
    // Effectively uncapped — dismissal is X/action-only. We re-open the
    // tooltip every time the dialog opens until the user explicitly dismisses,
    // so the per-visit cap shouldn't gate visibility.
    maxDisplayCount: 9999,
    precedence: 0,
    manualOpenOnly: true,
    showHighlight: true,
    feature: 'customShortcuts',
    variant: 'Body',
    maxWidth: '320px',
  },
  {
    id: TOOLTIP_IDS.VERSION_HISTORY,
    title: TEXT.VERSION_HISTORY.title,
    description: TEXT.VERSION_HISTORY.description,
    target: '[data-onboarding-anchor="version-history"]',
    routes: ['/project/*/view/*'],
    placement: 'bottom',
    feature: 'minorVersions',
    variant: 'Body',
    disableOnMobile: true,
    showAfterVisitNumber: 1,
    actionAlignment: 'end',
    action: {
      kind: 'command',
      command: 'open-version-history',
      label: TEXT.VERSION_HISTORY.viewHistoryLabel,
    },
  },
];
